import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowLeft, ArrowRight, ArrowUpRight, Trophy, Warning, X } from "@phosphor-icons/react";
import { GoalStatus } from "hooks/api/documentGeneration/useGetDocumentChecklist";
import { getNotIncludedReason, isGoalNotIncluded, type GoalsViewMode } from "./goalInclusion";
import AdditionalInformationSection, {
  AdditionalAttachment,
} from "./AdditionalInformationSection";
import OverrideChip from "./OverrideChip";

interface ReviewClinicalGoalsStepProps {
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  /** Display title of the document type */
  documentTitle: string;
  /** Client ID — used for the "Go to" deep link into the Goals & Targets page */
  clientId: string;
  /** Full goal list for the document */
  goals: GoalStatus[];
  /** Inclusion mode — ITP uses "withoutProgress" (baseline required), HAR uses "hoursAddendum" (baseline not required). */
  goalsView: GoalsViewMode;
  /** When true, block advancing unless at least one goal will be included/sent to MIRA. */
  requiresAtLeastOneGoal: boolean;
  /** Whether the goals are still loading */
  isLoading: boolean;
  /** Callback to return to the previous step */
  onBack: () => void;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when the user confirms the goal selections */
  onContinue: () => void;
  /** Free-text additional information to embed in the generated document */
  additionalInformation: string;
  /** Handler for additional information text changes */
  onAdditionalInformationChange: (value: string) => void;
  /** Image attachments to embed in the generated document */
  additionalFiles: AdditionalAttachment[];
  /** Handler for attachment list changes */
  onAdditionalFilesChange: (files: AdditionalAttachment[]) => void;
}

const goalRowSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "12px",
  px: 5,
  py: 4,
  gap: 3,
};

/**
 * Review Clinical Goals step of the Generate Document dialog.
 * Shown after the Review Assessments step. Always rendered for treatment plan docs.
 *
 * @param props
 * @param props.documentTitle - Display title of the document type
 * @param props.goals - Full goal list for the document
 * @param props.isLoading - Whether the goals are still loading
 * @param props.onBack - Callback to return to the previous step
 * @param props.onClose - Callback to close the dialog
 * @param props.onContinue - Callback when the user confirms the goal selections
 */
const ReviewClinicalGoalsStep: React.FC<ReviewClinicalGoalsStepProps> = ({
  documentTitle,
  clientId,
  goals,
  goalsView,
  requiresAtLeastOneGoal,
  isLoading,
  onBack,
  onClose,
  onContinue,
  additionalInformation,
  onAdditionalInformationChange,
  additionalFiles,
  onAdditionalFilesChange,
  isOverridden = false,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 11 }}>
        <CircularProgress />
      </Box>
    );
  }

  const newGoals = goals.filter((g) => !isGoalNotIncluded(g, goalsView));
  const notIncludedGoals = goals.filter((g) => isGoalNotIncluded(g, goalsView));
  const blockNoGoals = requiresAtLeastOneGoal && newGoals.length === 0;

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={onBack}
            aria-label="Back to review assessments"
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "10px",
              boxShadow: 1,
              p: "5px",
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h2">
              Review Clinical Goals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review clinical goals to be included in the {documentTitle}.
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} aria-label="Close dialog" size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pb: 7, pt: 0 }}>
        <Stack sx={{ mt: 7, gap: 6 }}>
          {isOverridden && <OverrideChip />}
          {blockNoGoals && (
            <Alert severity="warning">
              At least one goal must be included to generate this document.
            </Alert>
          )}
          <Stack sx={{ gap: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ display: "flex", alignItems: "center", gap: 3 }}
            >
              <Box component="span" sx={{ color: "info.main", display: "inline-flex" }}>
                <Trophy size={20} weight="duotone" />
              </Box>
              New goals ({newGoals.length})
            </Typography>
            {newGoals.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No new goals.
              </Typography>
            ) : (
              <Stack sx={{ gap: 3 }}>
                {newGoals.map((goal) => (
                  <Stack
                    key={goal.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={goalRowSx}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {goal.title}
                      </Typography>
                      {goal.description && (
                        <Typography variant="body2" color="text.secondary">
                          {goal.description}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontStyle="italic"
                      sx={{ flexShrink: 0 }}
                    >
                      {goal.goalType}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>

          {notIncludedGoals.length > 0 && (
            <Stack sx={{ gap: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ display: "flex", alignItems: "center", gap: 3 }}
              >
                <Box component="span" sx={{ color: "warning.main", display: "inline-flex" }}>
                  <Warning size={20} weight="duotone" />
                </Box>
                Goals not included ({notIncludedGoals.length})
              </Typography>
              <Stack sx={{ gap: 3 }}>
                {notIncludedGoals.map((goal) => {
                  const reason = getNotIncludedReason(goal, goalsView);
                  return (
                    <Stack
                      key={goal.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={goalRowSx}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={500}>
                          {goal.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {reason?.description}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        endIcon={<ArrowUpRight size={16} />}
                        onClick={() =>
                          window.open(
                            `/clients/${clientId}/goals-targets`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                        sx={{ flexShrink: 0 }}
                      >
                        {reason?.buttonText}
                      </Button>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          )}

          <AdditionalInformationSection
            additionalInformation={additionalInformation}
            onAdditionalInformationChange={onAdditionalInformationChange}
            files={additionalFiles}
            onFilesChange={onAdditionalFilesChange}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5 }}>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowRight size={16} />}
          onClick={onContinue}
          disabled={blockNoGoals && !isOverridden}
        >
          Continue To Confirm
        </Button>
      </DialogActions>
    </>
  );
};

export default ReviewClinicalGoalsStep;
